import * as React from "react";

import { usePlotPanelContext } from "../panelContext/usePlotPanelContext";
import { PlotManager } from "../PlotManager";
import { useViewport } from "../Viewport/useViewport";
import { Viewport } from "../Viewport/viewport";

export function useBroadcastLocalViewport(localViewport: Viewport) {
  // Track the previous viewport to identify when a change has occurred.
  const prevLocalViewport = React.useRef(localViewport);
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport, setSharedViewport] = useViewport();

  React.useEffect(
    function onLocalViewportChanged() {
      if (prevLocalViewport.current.isOlderThan(localViewport)) {
        if (viewportSyncMode) {
          setSharedViewport(
            Viewport.combine(sharedViewport, localViewport, viewportSyncMode),
          );
        }
      }
      prevLocalViewport.current = localViewport;
    },
    [localViewport, viewportSyncMode, sharedViewport, setSharedViewport],
  );
}

export function useOnSyncEnabled(localViewport: Viewport) {
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport, setSharedViewport] = useViewport();
  // Track the previous viewport to identify when a change has occurred.
  const prevSyncMode = React.useRef(viewportSyncMode);

  // When this chart starts synchronizing its viewport, sync all other charts' viewports to this one.
  React.useEffect(
    function onViewportSyncEnabled() {
      if (viewportSyncMode && viewportSyncMode !== prevSyncMode.current) {
        setSharedViewport(
          // Because the viewport was just set to synchronize, broadcast
          // this panel's current viewport as the latest viewport.
          Viewport.combine(
            sharedViewport,
            localViewport,
            viewportSyncMode,
            Viewport.latestDebounceId(),
          ),
        );
      }
      prevSyncMode.current = viewportSyncMode;
    },
    [localViewport, sharedViewport, viewportSyncMode, setSharedViewport],
  );
}

export function useApplySharedViewport(
  plotManager: PlotManager | null,
  isDragging: React.RefObject<boolean>,
) {
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport] = useViewport();
  React.useEffect(
    function applySharedViewport() {
      if (!viewportSyncMode || isDragging.current) {
        return;
      }
      plotManager?.applyViewport(sharedViewport, viewportSyncMode);
    },
    [sharedViewport, viewportSyncMode, plotManager, isDragging],
  );
}
